// /compare/dubsado: Pixally vs Dubsado.
//
// Read the header of honeybook.js first: every rule there applies here. Dubsado's
// facts, with sources and exact words: docs/geo/compare-honeybook-alternatives-evidence.md.
// Dubsado wins on bank-transfer fees and on the cost of a small team, and the page
// says so. Do not use the 'fees' drawing here: it shows Pixally's cap as the lower
// one, which against Dubsado it is not.

export const ROUTE = '/compare/dubsado';
export const UPDATED_AT = '2026-10-05';
export const CHECKED_AT = 'October 1, 2026';
export const CHECKED_LABEL = 'Dubsado facts checked';
export const HERO_ART = 'versus';

export const TITLE = 'Pixally vs Dubsado';
export const SEO_TITLE = 'Pixally vs Dubsado for Wedding Photographers and Videographers';
export const SEO_DESCRIPTION =
  'How Pixally and Dubsado compare on price, payment fees, galleries, film delivery, team members and second shooters, with every Dubsado fact sourced.';

export const LEDE =
  'Dubsado is client management software that handles proposals, contracts, invoices, forms and workflows, and leaves photo and film delivery to another tool. Pixally is a CRM built for wedding photographers and videographers, launching on January 1, 2027, with galleries, film delivery, second shooters and post-production tracking in the same place.';

const DUBSADO_PRICING = 'https://www.dubsado.com/pricing';
const DUBSADO_FEES = 'https://help.dubsado.com/en/articles/15920643-dubsado-payments-processing-fees';
const DUBSADO_LINKS =
  'https://help.dubsado.com/en/articles/1796219-share-a-photo-gallery-in-the-client-portal-in-2-0';

export const BODY = [
  {
    type: 'paragraph',
    text: 'If you already deliver through a gallery platform you like and want a flexible, established CRM today, Dubsado is a strong choice. If you want delivery, your crew and your edit in the same system as your bookings, the differences below matter.',
  },
  { type: 'heading', level: 2, text: 'Side by side' },
  {
    type: 'paragraph',
    text: `The Dubsado column comes from Dubsado's own published pages, checked ${CHECKED_AT}.`,
  },
  {
    type: 'table',
    label: 'Pixally and Dubsado compared',
    head: ['', 'Pixally', 'Dubsado'],
    rows: [
      ['Available', 'From January 1, 2027', 'Now'],
      ['Plans, billed monthly', 'Free $0, Basic $39, Pro $69, Studio $119', 'Starter $35, Premier $55'],
      ['Plans, billed yearly', 'Basic $33, Pro $58, Studio $99 a month', 'Starter $335, Premier $525 a year'],
      ['Free plan or trial', 'A 14-day free trial with no card, then the Free plan: up to 5 active booked projects', 'No free plan. A 21-day trial of Premier'],
      ['US cards', '2.9% + 30¢ on paid plans (3.4% + 30¢ on Free)', '2.9% + $0.30'],
      ['Bank transfer (ACH)', '1% (Free, Basic); 0.8% capped at $12 (Pro) or $8 (Studio)', '0.8%, capped at $5'],
      ['Client galleries', 'Photo, video and hybrid galleries built in, on paid plans', 'Not built in: you link a gallery hosted elsewhere from the client portal'],
      ['Film delivery', 'Built in: 50 GB to start on paid plans, then packs; no limit on film length', 'Not built in'],
      ['Scheduling and automations', 'On Basic and up (scheduling through Calendly)', 'On Premier'],
      ['Team members', '1, 1, 2, unlimited; more for $9 a month each', 'Owner plus 3 free; 4 to 10 users $25 a month'],
      ['Second shooters and contractors', 'Free contractor accounts; second shooter management and a contractor portal on Pro and Studio', "Not described on Dubsado's pricing page"],
      ['Post-production tracking', 'On Pro and Studio', "Not described on Dubsado's pricing page"],
      ['More than one brand', '1, 1, 2, unlimited brands', '$10 a month per extra brand'],
    ],
  },
  { type: 'heading', level: 2, text: 'When Dubsado is the better choice' },
  {
    type: 'list',
    items: [
      'It exists today. Pixally launches on January 1, 2027.',
      'Lower bank-transfer fees. Dubsado charges 0.8% capped at $5; Pixally caps at $12 on Pro and $8 on Studio, and charges 1% uncapped on Free and Basic.',
      'A small team costs less. Dubsado includes three extra users free, so four people cost $35 a month on Starter. On Pixally Basic the same four people cost $66 a month ($39 plus three team members at $9).',
      'A lower yearly price on its entry plan. Starter is $335 a year, against $390 for Pixally Basic billed yearly.',
      'You already deliver through a gallery platform you are happy with, and do not want to move it.',
    ],
  },
  { type: 'heading', level: 2, text: 'When Pixally is the better choice' },
  {
    type: 'paragraph',
    text: 'Pixally is the better choice if you want galleries and films delivered from your CRM, run second shooters and editors, or want to start on a free plan.',
  },
  { type: 'heading', level: 3, text: 'Galleries and films' },
  { type: 'art', name: 'film' },
  {
    type: 'paragraph',
    text: `Dubsado does not host galleries or films: [you add a link to a gallery hosted elsewhere](${DUBSADO_LINKS}) in the client portal, so delivery is a second subscription. Pixally delivers photos, films and hybrid galleries from the same project as the booking, with no limit on film length, 50 GB of film delivery to start on every paid plan and more in packs that never expire.`,
  },
  { type: 'heading', level: 3, text: 'Second shooters and contractors' },
  { type: 'art', name: 'crew' },
  {
    type: 'paragraph',
    text: "In Pixally a second shooter does not need a team seat. They get a [free contractor account](/contractor-terms) that works across every studio they shoot for, with their availability, travel fee and tax form in one place, and another studio never sees what you pay them. Second shooter management and the contractor portal are on Pro (up to 10) and Studio (unlimited).",
  },
  { type: 'heading', level: 3, text: 'Post-production' },
  { type: 'art', name: 'pipeline' },
  {
    type: 'paragraph',
    text: "Pixally's post-production management (Pro and Studio) follows the stages of a wedding edit: uploads, culling and selects, edits assigned to your team or outside editors with due dates, review, client revisions and delivery.",
  },
  { type: 'heading', level: 3, text: 'A free plan' },
  {
    type: 'paragraph',
    text: "Pixally starts with a 14-day free trial, no card needed, and its Free plan then covers up to 5 active booked projects with proposals, contracts and payments. Dubsado has a 21-day trial and then a paid plan.",
  },
  { type: 'heading', level: 2, text: 'Switching from Dubsado' },
  { type: 'art', name: 'switching' },
  {
    type: 'paragraph',
    text: 'Pixally will move your last 12 months of active client projects across for free, and turn your existing documents into Pixally templates. Winter, when the season is quiet, is the easiest time to switch.',
  },
];

export const FAQ = [
  {
    q: 'Does Dubsado have client galleries?',
    a: 'No. Dubsado lets you add a link to a gallery hosted elsewhere to a project in the client portal. Pixally has photo, video and hybrid galleries built in on paid plans.',
  },
  {
    q: 'Is Dubsado cheaper than Pixally?',
    a: 'For a small team, usually yes: Dubsado includes three extra users free, and its Starter plan is $335 a year against $390 for Pixally Basic. Dubsado also has the lower bank-transfer fee, 0.8% capped at $5. Pixally has a free plan, and includes galleries and film delivery that Dubsado leaves to another subscription.',
  },
  {
    q: 'What does Dubsado charge to process payments?',
    a: 'Through Dubsado Payments in the US, 2.9% + $0.30 for US cards, 4.4% + $0.30 for international cards, and 0.8% for bank transfers, capped at $5.',
  },
  {
    q: 'Can I move from Dubsado to Pixally?',
    a: 'Yes. Pixally moves your last 12 months of active projects across for free, on every plan.',
  },
  {
    q: 'Is Pixally available yet?',
    a: 'No. Pixally launches on January 1, 2027. You can join the waitlist at pixally.com.',
  },
];

export const SOURCES = [
  { label: 'Dubsado pricing', url: DUBSADO_PRICING },
  { label: 'Dubsado Payments processing fees (July 14, 2026)', url: DUBSADO_FEES },
  { label: 'Dubsado: share a photo gallery in the client portal', url: DUBSADO_LINKS },
  { label: 'Pixally plans and fees: the Pixally pricing table', url: null },
  { label: 'Pixally contractor accounts: Contractor Terms of Use, Section 5.1', url: '/contractor-terms' },
];
